import Locale from '@formatjs/intl-locale';
import { getMultilingualLocalePrefix, getLanguageAlternates, supportedDomains } from '@hub/libs/i18n.helper';
import { TRANSLATION_CONFIGURATION, LANG_FR_FR } from './constants';
import type { KetteEinbauArt } from '@hub/models/api/shopping-cart.types';

// export for testing only
export const IGUS_TOOLS_REDIRECT = (url = '', locale = TRANSLATION_CONFIGURATION.fallbackLocale) => {
  // get correct domain for locale, if it cant be found fallback to other locales:
  let currentSupportedDomainForIgusToolsRedirect =
    supportedDomains.find(supportedDomain => supportedDomain.locale === locale) ?? null;

  if (!currentSupportedDomainForIgusToolsRedirect) {
    // not a supported url. redirect to hardcoded fallback eu
    currentSupportedDomainForIgusToolsRedirect =
      supportedDomains.find(supportedDomain => supportedDomain.locale === TRANSLATION_CONFIGURATION.fallbackLocale) ??
      null;
  }
  if (!currentSupportedDomainForIgusToolsRedirect) {
    return url;
  }

  const { region = 'DE', language } = new Locale(locale);

  return `https://${currentSupportedDomainForIgusToolsRedirect.domain}${url}&l=${language}&c=${region}`;
};

export const IGUS_REWRITE = (url = '', locale = TRANSLATION_CONFIGURATION.fallbackLocale) => {
  if (url.indexOf('http') === 0) {
    return url;
  }

  // get correct domain for locale, if it cant be found fallback to other locales:
  let supportedDomainIgusRewrite = supportedDomains.find(supportedDomain => supportedDomain.locale === locale) ?? null;
  if (!supportedDomainIgusRewrite) {
    const defaultUrl = getLanguageAlternates().find(alternate => alternate.hrefLang === locale);

    if (defaultUrl) {
      return `${defaultUrl.href}${url}${defaultUrl.L ? `?L=${defaultUrl.L}` : ''}`;
    }
    // not a default url or a supported url. redirect to hardcoded fallback eu
    supportedDomainIgusRewrite =
      supportedDomains.find(supportedDomain => supportedDomain.locale === TRANSLATION_CONFIGURATION.fallbackLocale) ??
      null;
  }

  // actually this should never happen, because fallback locales are hardcoded constants (de-DE and en) which should always be present
  // in the supportedDomains array. If they are not there anymore, or have changed, the world must be burning. (in other words, we have a test to assure they're there)
  if (!supportedDomainIgusRewrite) {
    return url;
  }

  return `https://${supportedDomainIgusRewrite.domain}${url}`;
};

const getContactRequestUrl = (requestPath: string, locale: string) =>
  new URL(`${getMultilingualLocalePrefix(locale)}/l/service/contact/${requestPath}`, IGUS_REWRITE('', locale)).href;

const lifetimeCalculatorAppModes = {
  freitragend: 'UnsupportedWithSupportedLowerRun',
  gleitend: 'Gliding',
  haengend: 'UnsupportedWithUnsupportedLowerRun',
};

export const EXTERNAL_LINKS = {
  SHOP_HOMEPAGE: (locale = TRANSLATION_CONFIGURATION.fallbackLocale) => IGUS_REWRITE('', locale),
  INQUIRY_INDIVIDUAL_COMPONENT: (locale = TRANSLATION_CONFIGURATION.fallbackLocale) =>
    getContactRequestUrl('component-request', locale),
  NEWS_2020: (locale = TRANSLATION_CONFIGURATION.fallbackLocale) => IGUS_REWRITE('/info/news-2020-overview', locale),
  MYIGUS: (locale = TRANSLATION_CONFIGURATION.fallbackLocale) =>
    IGUS_REWRITE('/myigus/default.aspx?PAGE=myigus', locale),
  SEARCH: (locale = TRANSLATION_CONFIGURATION.fallbackLocale) => IGUS_REWRITE('/search?q=', locale),
  EPLAN: (variantSku: string) => `https://dataportal.eplan.com/part-details/igus.${variantSku}`,
  EXPERT_TOOL: (locale = TRANSLATION_CONFIGURATION.fallbackLocale) =>
    IGUS_REWRITE('/iproSvc/appredirect.aspx?app=igubalexperte', locale),
  ZUKEN: (variantSku: string) => `https://zuken.partcommunity.com/?catalog=igus&part=${variantSku}`,
  CAD: (
    variantSku: string,
    locale: string,
    verfahrweg?: number,
    effektiveLaenge?: number,
    numGlider?: number,
    einbauArt?: KetteEinbauArt
  ) => {
    // igus-cad is not working with EU locales, they must exceptionally be transformed to en-GB and es-ES
    const getModifiedLocale = () => {
      switch (locale) {
        case 'en':
          return 'en-GB';
        case 'es':
          return 'es-ES';
        default:
          return locale;
      }
    };

    const baseSearchParams = new URLSearchParams({
      cul: getModifiedLocale(),
      ArtNr: variantSku,
      mandant: 'INT',
      parammode: '',
    }).toString();

    const customParamsMap: Record<string, string> = {
      'Verfahrweg S': verfahrweg ? verfahrweg.toString() : '',
      'gewünschte Kettenlänge': effektiveLaenge ? effektiveLaenge.toString() : '',
      'Anzahl Kettenglieder': numGlider ? numGlider.toString() : '',
      Anwendungsfall: einbauArt ?? '',
    };

    const formattedCustomParams = Object.entries(customParamsMap)
      .filter(([_, value]) => value !== '')
      .map(([key, value]) => new URLSearchParams({ [key]: value }).toString())
      .join('|');

    const queryString = `${baseSearchParams}${formattedCustomParams}`;

    return `https://www.igus-cad.com/default.aspx?${queryString}`;
  },
  SAMPLE: (variantSku: string, description: string, locale = TRANSLATION_CONFIGURATION.fallbackLocale) =>
    `${getContactRequestUrl('request-product-sample', locale)}?T=t&ARTNR=${variantSku}&BEZ=${description}`,
  CART: (locale = TRANSLATION_CONFIGURATION.fallbackLocale) => IGUS_REWRITE('/cart', locale),
  CF_CASE_INFO: (locale = TRANSLATION_CONFIGURATION.fallbackLocale) => IGUS_REWRITE('/info/chainflex-case', locale),
  CF_CASE_REFILL_INFO: (locale = TRANSLATION_CONFIGURATION.fallbackLocale) =>
    IGUS_REWRITE('/info/cfcase-refill', locale),
  MARKETING: {
    TECH_UP_COST_DOWN: (locale = TRANSLATION_CONFIGURATION.fallbackLocale) =>
      IGUS_REWRITE('/info/tech-up-cost-down', locale),
    EXHIBITION: (locale = TRANSLATION_CONFIGURATION.fallbackLocale) => IGUS_REWRITE('/info/news-2022-overview', locale),
  },
  CHAINFLEX_LIFETIME_CALC: (variantSku: string, locale = TRANSLATION_CONFIGURATION.fallbackLocale) =>
    IGUS_TOOLS_REDIRECT(`/iproSvc/appredirect.aspx?app=koplacsl&partNumber=${variantSku}`, locale),
  ECHAIN_LIFETIME_CALC: (
    partNumber: string,
    travel: number,
    installType: KetteEinbauArt,
    locale = TRANSLATION_CONFIGURATION.fallbackLocale
  ) =>
    IGUS_TOOLS_REDIRECT(
      `/iProSvc/appredirect.aspx?app=koplaesl&partNumber=${partNumber}&travel=${travel / 1000}&applicationMode=${
        lifetimeCalculatorAppModes[installType]
      }`,
      locale
    ),
  COPPER_CALC: (
    variantSku: string,
    iproProdId: string,
    iproPrgrId: string,
    locale = TRANSLATION_CONFIGURATION.fallbackLocale
  ) =>
    IGUS_REWRITE(`/ChainflexTools/CopperSupplement?prgr=${iproPrgrId}&prod=${iproProdId}&partno=${variantSku}`, locale),
  ADD_TO_ECHAIN_CONFIG: (variantSku: string, locale = TRANSLATION_CONFIGURATION.fallbackLocale) =>
    IGUS_REWRITE(`/ChainflexTools/CablePool?artNr=${variantSku}`, locale),
  ADD_TO_WISHLIST: (variantSku: string, locale = TRANSLATION_CONFIGURATION.fallbackLocale) =>
    IGUS_REWRITE(`/myigus/default.aspx?page=login&addcustar=${variantSku}`, locale),
  NEW_MY_IGUS_LOGIN: (locale = TRANSLATION_CONFIGURATION.fallbackLocale) =>
    IGUS_REWRITE(`/myigus/${locale}/login`, locale),
  CONTACT_PERSON: {
    SEE_ALL_CONTACT: (locale = TRANSLATION_CONFIGURATION.fallbackLocale) => IGUS_REWRITE('/contact', locale),
    SEE_ALL_CONTACT_FR: (locale = LANG_FR_FR) => IGUS_REWRITE('/info/contact-igus-france', locale),
    WRITE_MESSAGE: (ADRE: string, SPARTE: string, PATY: string, locale = TRANSLATION_CONFIGURATION.fallbackLocale) =>
      IGUS_REWRITE(`/myigus/default.aspx?page=support&ADRE=${ADRE}&SPARTE=${SPARTE}&PATY=${PATY}`, locale),
    CONTACT_FORM: (ADRE: number, locale = TRANSLATION_CONFIGURATION.fallbackLocale) =>
      IGUS_REWRITE(`/myigus/default.aspx?page=form_contact&adre=${ADRE}`, locale),
  },
  SALES: {
    SALES_FR: (locale = LANG_FR_FR) => IGUS_REWRITE('/contact/sales', locale),
  },
  E_SKIN_CONFIGURATOR: () => 'https://eskin-flat-pod-configurator.igus.tools/',
  BALL_BEARING_CALC: (variantSku: string, locale = TRANSLATION_CONFIGURATION.fallbackLocale) =>
    IGUS_TOOLS_REDIRECT(`/iProSvc/appredirect.aspx?app=koplaxlr&a=${variantSku}`, locale),
  BALL_BEARING_CONFIG: (variantSku: string, locale = TRANSLATION_CONFIGURATION.fallbackLocale) =>
    IGUS_TOOLS_REDIRECT(`/iProSvc/appredirect.aspx?app=koplaxre&a=${variantSku}`, locale),
  READYCABLE_PRODUCT_FINDER: (variantSku: string, locale = TRANSLATION_CONFIGURATION.fallbackLocale) =>
    IGUS_TOOLS_REDIRECT(`/iproSvc/appredirect.aspx?app=koplarcp&partNumber=${variantSku}`, locale),
  UNSUPPORTED_ECHAIN_INFO: (locale = TRANSLATION_CONFIGURATION.fallbackLocale) =>
    IGUS_REWRITE('/info/energy-chains-short-travel-unsupported', locale),
  GLIDING_ECHAIN_INFO: (locale = TRANSLATION_CONFIGURATION.fallbackLocale) =>
    IGUS_REWRITE('/info/energy-chains-gliding', locale),
  HANGING_ECHAIN_INFO: (locale = TRANSLATION_CONFIGURATION.fallbackLocale) =>
    IGUS_REWRITE('/info/energy-chains-vertical-hanging', locale),
  DRYSPIN_CONFIGURATOR: (
    variantSku: string,
    quantity: number,
    length: number,
    locale = TRANSLATION_CONFIGURATION.fallbackLocale
  ) =>
    IGUS_REWRITE(
      `/iProSvc/appredirect.aspx?app=kopladsk&a=${variantSku}&quantity=${quantity}&length=${length}`,
      locale
    ),
};
